import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DateTime } from "luxon";

// Week day-strip with per-day class counts. The pills double as scroll-nav for
// the day sections below. Shared by the mobile filter bar and the desktop
// Day-mode header.
function ClassDayStrip({ cls, scrollSpy }) {
  const { weekDates, classesByDate, todayISO, canGoPrevWeek, goPrevWeek, goNextWeek, weekHasClasses } = cls;
  const { activeDay, scrollToDay } = scrollSpy;

  const renderDayPill = (d) => {
    const dt = DateTime.fromISO(d);
    const count = (classesByDate.get(d) || []).length;
    const active = d === activeDay;
    const isToday = d === todayISO;
    const disabled = count === 0;
    // Today keeps its full weight even with nothing on it. It is the client's
    // anchor in the week — faded out behind six other faded days, it stops
    // telling them where they are, which is the moment they need it most.
    const dimmed = disabled && !isToday;
    return (
      <ButtonBase
        key={d}
        onClick={() => scrollToDay(d)}
        disabled={disabled}
        sx={{
          flexShrink: 0,
          minWidth: "3rem",
          py: 0.75,
          px: 0.5,
          borderRadius: 2,
          borderStyle: "solid",
          borderWidth: isToday ? "0.125rem" : "0.09375rem",
          borderColor: active || isToday ? "primary.main" : "divider",
          bgcolor: active ? "primary.main" : "#fff",
          color: active ? "#fff" : dimmed ? "text.disabled" : "text.primary",
          opacity: dimmed ? 0.5 : 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transition: "all 0.15s ease"
        }}
      >
        <Typography
          sx={{ fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1, color: "inherit" }}
        >
          {dt.toFormat("ccc")}
        </Typography>
        <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.3, color: "inherit" }}>{dt.day}</Typography>
        {/* A week can straddle two months, and "30 31 1 2" says nothing about
            where one ended and the next began. */}
        <Typography
          sx={{
            fontSize: "0.5rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            lineHeight: 1,
            mt: 0.25,
            color: "inherit",
            opacity: 0.75
          }}>
          {dt.toFormat("LLL")}
        </Typography>
        {/* Every day carries its count, zeros included. Blank meant two
            different things — "nothing on" and "nothing loaded" — and a week of
            blanks reads as a broken page rather than a quiet week, which is
            exactly when the client needs to know to look forward. */}
        <Typography
          sx={{
            fontSize: "0.55rem",
            fontWeight: 600,
            lineHeight: 1,
            minHeight: "0.7em",
            // Green wherever it means something: a real count, or today's zero,
            // which is the one zero worth reading — it says the quiet week is
            // this week, so look forward.
            color: active ? "rgba(255,255,255,0.85)" : count > 0 || isToday ? "primary.main" : "text.disabled"
          }}
        >
          {count}
        </Typography>
      </ButtonBase>
    );
  };

  const navButtonSx = {
    border: "1px solid",
    borderColor: "primary.main",
    color: "primary.main",
    borderRadius: "50%",
    width: "1.875rem",
    height: "1.875rem",
    flexShrink: 0,
    "&:hover": { bgcolor: "primary.main", color: "#fff" },
    "&.Mui-disabled": { borderColor: "divider", color: "text.disabled" }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <IconButton size="small" onClick={goPrevWeek} disabled={!canGoPrevWeek} aria-label="Previous week" sx={navButtonSx}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Box sx={{ display: "flex", gap: 0.75, overflowX: "auto", flex: 1, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
        {weekDates.map((d) => renderDayPill(d))}
      </Box>
      {/* With nothing on this week, this arrow IS the next step — a club whose
          classes start on Monday leaves the client staring at an empty Sunday
          otherwise. Filled so it reads as the way on, not as decoration. */}
      <IconButton
        size="small"
        onClick={goNextWeek}
        aria-label="Next week"
        sx={{ ...navButtonSx, ...(weekHasClasses ? {} : { bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }) }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export default ClassDayStrip;
